import { Box, Collapse, Divider, Drawer, Icon, List, ListItemButton, ListItemIcon, ListItemText, Typography, useMediaQuery, useTheme } from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppThemeContext, useDrawerContext } from "../../../context";
import logo from "../../../assets/logo_garantti.png";
import { Environment } from "../../environment";
import { useLayoutEffect, useState } from "react";
import { MenuItem } from "../../constants/menu";

interface IListItemLinkProps {
	to: string;
	icon: string;
	label: string;
	onClick?: () => void;
	nestedLevel?: number;
	selected?: boolean;
}

const ListItemLink: React.FC<IListItemLinkProps> = ({ to, icon, label, onClick, nestedLevel = 0, selected }) => {
	const navigate = useNavigate();
	const theme = useTheme();
	const accentColor = theme.palette.primary.main;
	const iconBgColor = alpha(accentColor, 0.12);
	const hoverBg = alpha(accentColor, 0.08);
	const selectedBg = alpha(accentColor, 0.16);
	const isRootItem = nestedLevel === 0;
	const paddingLeft = isRootItem ? 1.35 : 1.75 + nestedLevel * 0.85;
	const verticalPadding = isRootItem ? 0.6 : 0.45;

	const handleClick = () => {
		navigate(to, { replace: true });
		onClick?.();
	};

	return (
		<ListItemButton
			selected={selected}
			onClick={handleClick}
			sx={{
				pl: paddingLeft,
				py: verticalPadding,
				mb: 0.15,
				borderRadius: 1.5,
				color: theme.palette.text.secondary,
				transition: "background-color 0.15s ease",
				minHeight: isRootItem ? 40 : 34,
				"&:hover": {
					backgroundColor: hoverBg,
				},
				"&.Mui-selected": {
					backgroundColor: selectedBg,
					color: accentColor,
				},
				"&.Mui-selected:hover": {
					backgroundColor: alpha(accentColor, 0.22),
				},
			}}
		>
			{isRootItem && (
				<ListItemIcon
					sx={{
						minWidth: 0,
						mr: 1.25,
						width: 32,
						height: 32,
						borderRadius: 1,
						backgroundColor: iconBgColor,
						color: accentColor,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						"& .MuiIcon-root": { fontSize: 18 },
					}}
				>
					<Icon>{icon}</Icon>
				</ListItemIcon>
			)}
			<ListItemText primary={label} primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }} />
		</ListItemButton>
	);
};

const normalizeLabel = (label: string) => label.normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, "-").toLowerCase();

const getMenuItemKey = (item: MenuItem, index: number, parentKey: string) => {
	const labelSegment = normalizeLabel(item.label);
	const suffix = item.path ?? `group-${index}`;
	return parentKey ? `${parentKey}__${labelSegment}-${suffix}` : `${labelSegment}-${suffix}`;
};

const isMenuItemActive = (item: MenuItem, pathname: string): boolean => {
	if (item.path && pathname.startsWith(item.path)) return true;
	if (!item.children) return false;

	return item.children.some((child) => isMenuItemActive(child, pathname));
};

const pathMatches = (pathname: string, path: string) => {
	if (!path) return false;
	if (pathname === path) return true;
	if (path === "/") return pathname.startsWith("/");
	return pathname.startsWith(`${path}/`);
};

const getBestMatchingLeafPath = (items: MenuItem[], pathname: string): string | null => {
	let best: string | null = null;
	let bestLen = -1;

	const walk = (nodes: MenuItem[]) => {
		nodes.forEach((node) => {
			if (node.path && pathMatches(pathname, node.path)) {
				const len = node.path.length;
				if (len > bestLen) {
					best = node.path;
					bestLen = len;
				}
			}

			if (node.children && node.children.length) {
				walk(node.children);
			}
		});
	};

	walk(items);
	return best;
};

const getParentKeyFromCompositeKey = (key: string) => {
	const lastSeparatorIndex = key.lastIndexOf("__");
	return lastSeparatorIndex === -1 ? "" : key.substring(0, lastSeparatorIndex);
};

const collectExpandedKeys = (items: MenuItem[], pathname: string, parentKey = "", accumulator: Record<string, boolean> = {}) => {
	let hasActiveChildOnLevel = false;

	items.forEach((item, index) => {
		const itemKey = getMenuItemKey(item, index, parentKey);
		const children = item.children ?? [];
		const childHasActive = children.length ? collectExpandedKeys(children, pathname, itemKey, accumulator) : false;
		const selfIsActive = isMenuItemActive(item, pathname);

		if (children.length) {
			accumulator[itemKey] = selfIsActive || childHasActive;
		}

		if (selfIsActive || childHasActive) {
			hasActiveChildOnLevel = true;
		}
	});

	return hasActiveChildOnLevel;
};

interface ISidebarProps {
	children: React.ReactNode;
}

export const Sidebar: React.FC<ISidebarProps> = ({ children }) => {
	const theme = useTheme();
	const smDown = useMediaQuery(theme.breakpoints.down("sm"));
	const version = Environment.VERSION;
	const environmentLabel = Environment.AMBIENTE?.trim() ?? "";
	const normalizedEnvironment = environmentLabel.toLowerCase();
	const shouldShowEnvironmentBadge = Boolean(environmentLabel) && !["prod", "production", "producao", "produção"].includes(normalizedEnvironment);
	const accentColor = theme.palette.primary.main;
	const groupBgColor = alpha(accentColor, 0.08);
	const groupHoverColor = alpha(accentColor, 0.14);
	const dividerColor = alpha(theme.palette.text.primary, 0.12);
	const iconBgColor = alpha(accentColor, 0.12);
	const environmentColorMap: Record<string, string> = {
		dev: theme.palette.info.main,
		development: theme.palette.info.main,
		hml: theme.palette.warning.main,
		homolog: theme.palette.warning.main,
		homologacao: theme.palette.warning.main,
		homologação: theme.palette.warning.main,
		staging: theme.palette.warning.main,
		stage: theme.palette.warning.main,
		qa: theme.palette.secondary.main,
		test: theme.palette.secondary.main,
	};
	const environmentBadgeColor = environmentColorMap[normalizedEnvironment] ?? theme.palette.grey[700];

	const { isDrawerOpen, toggleDrawerOpen, drawerOptions } = useDrawerContext();
	const { toggleTheme } = useAppThemeContext();
	const { pathname } = useLocation();
	const activeLeafPath = getBestMatchingLeafPath(drawerOptions, pathname);
	const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});

	useLayoutEffect(() => {
		const defaultExpanded: Record<string, boolean> = {};
		collectExpandedKeys(drawerOptions, pathname, "", defaultExpanded);
		setExpandedItems(defaultExpanded);
	}, [drawerOptions, pathname]);

	const handleToggleCollapse = (key: string, parentKey: string) => {
		setExpandedItems((previousState) => {
			const isCurrentlyOpen = previousState[key] ?? false;
			const nextValue = !isCurrentlyOpen;
			const updatedState: Record<string, boolean> = { ...previousState };
			const allKeys = Object.keys(updatedState);

			const collapseBranch = (targetKey: string) => {
				allKeys.forEach((nestedKey) => {
					if (nestedKey.startsWith(`${targetKey}__`)) {
						updatedState[nestedKey] = false;
					}
				});
			};

			allKeys.forEach((existingKey) => {
				const existingParent = getParentKeyFromCompositeKey(existingKey);
				if (existingKey !== key && existingParent === parentKey) {
					updatedState[existingKey] = false;
					collapseBranch(existingKey);
				}
			});

			if (!nextValue) {
				collapseBranch(key);
			}

			updatedState[key] = nextValue;

			return updatedState;
		});
	};

	const renderMenuItems = (items: MenuItem[], level = 0, parentKey = "") =>
		items.map((item, index) => {
			const itemKey = getMenuItemKey(item, index, parentKey);
			const hasChildren = Boolean(item.children && item.children.length);

			if (hasChildren) {
				const isExpanded = expandedItems[itemKey] ?? false;
				const isDirectlySelected = item.path ? pathname.startsWith(item.path) : false;

				return (
					<Box key={itemKey} sx={{ mb: 0.5 }}>
						<ListItemButton
							onClick={() => handleToggleCollapse(itemKey, parentKey)}
							sx={{
								pl: 1.35 + level * 1.2,
								py: level === 0 ? 0.6 : 0.45,
								minHeight: level === 0 ? 40 : 34,
								borderRadius: 1.5,
								mb: 0.15,
								backgroundColor: isDirectlySelected ? groupBgColor : "transparent",
								color: theme.palette.text.primary,
								transition: "background-color 0.2s ease",
								"&:hover": {
									backgroundColor: groupHoverColor,
								},
							}}
						>
							{level === 0 && (
								<ListItemIcon
									sx={{
										minWidth: 0,
										mr: 1.25,
										width: 32,
										height: 32,
										borderRadius: 1,
										backgroundColor: iconBgColor,
										color: accentColor,
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										"& .MuiIcon-root": { fontSize: 20 },
									}}
								>
									<Icon>{item.icon}</Icon>
								</ListItemIcon>
							)}
							<ListItemText sx={{ ml: level === 0 ? 0 : 0.5 }} primary={item.label} primaryTypographyProps={{ fontWeight: 400, fontSize: 14 }} />
							{isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
						</ListItemButton>
						<Collapse in={isExpanded} timeout="auto" unmountOnExit>
							<Box sx={{ borderLeft: `1px solid ${dividerColor}`, ml: 2.5, pl: 1, py: 0.25 }}>
								<List component="div" disablePadding>
									{renderMenuItems(item.children ?? [], level + 1, itemKey)}
								</List>
							</Box>
						</Collapse>
					</Box>
				);
			}

			if (!item.path) return null;
			const isSelected = activeLeafPath ? activeLeafPath === item.path : pathMatches(pathname, item.path);

			return (
				<ListItemLink
					key={itemKey}
					icon={item.icon}
					to={item.path}
					label={item.label}
					onClick={smDown ? toggleDrawerOpen : undefined}
					nestedLevel={level}
					selected={isSelected}
				/>
			);
		});

	return (
		<>
			{/* <Drawer open={isDrawerOpen} variant={smDown ? 'temporary' : 'permanent'} onClose={toggleDrawerOpen}> */}
			<Drawer open={isDrawerOpen} variant={isDrawerOpen ? "permanent" : "persistent"}>
				<Box width={isDrawerOpen ? theme.spacing(28) : 0} height="100%" display="flex" flexDirection="column">
					<Box width="100%" height={theme.spacing(16)} display="flex" alignItems="center" justifyContent="center" sx={{ background: "#7924c7" }}>
						<img src={logo} alt="Logo" height={"130%"} />
					</Box>

					<Divider />

					<Box
						flex={1}
						sx={{
							color: "#666",
							overflowY: "auto",

							/* Scrollbar - Chrome, Safari, Edge (webkit) */
							"&::-webkit-scrollbar": {
								width: 8, // Largura
							},
							"&::-webkit-scrollbar-track": {
								background: "#f1f1f1",
								borderRadius: 8, // Arredonda o trilho
							},
							"&::-webkit-scrollbar-thumb": {
								backgroundColor: "#c1c1c1",
								borderRadius: 8, // Arredonda o thumb
								backgroundClip: "padding-box",
								border: "2px solid #f1f1f1", // Cria espaço em torno, ressaltando a borda
							},
							"&::-webkit-scrollbar-thumb:hover": {
								backgroundColor: "#b0b0b0",
							},

							/* Scrollbar - Firefox */
							scrollbarWidth: "thin", // Deixa a barra fina
							scrollbarColor: "#c1c1c1 #f1f1f1", // thumb e track (cor do polegar e do trilho)
						}}
					>
						<List component="nav" disablePadding sx={{ px: 1, py: 1.5 }}>
							{renderMenuItems(drawerOptions)}
						</List>
					</Box>

					<Box>
						<List component="nav">
							<ListItemButton onClick={toggleTheme}>
								<ListItemIcon>
									<Icon> dark_mode</Icon>
								</ListItemIcon>
								<ListItemText
									primary={
										<Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
											<Typography variant="body2" sx={{ color: "gray" }}>
												{version}
											</Typography>
											{shouldShowEnvironmentBadge && (
												<Box
													sx={{
														px: 1,
														py: 0.25,
														borderRadius: 999,
														backgroundColor: alpha(environmentBadgeColor, 0.16),
														border: `1px solid ${alpha(environmentBadgeColor, 0.3)}`,
														color: environmentBadgeColor,
														fontSize: 11,
														fontWeight: 700,
														letterSpacing: 0.4,
														textTransform: "uppercase",
													}}
												>
													{environmentLabel}
												</Box>
											)}
										</Box>
									}
								/>
								{/* 2.01a */}
							</ListItemButton>
						</List>
					</Box>
				</Box>
			</Drawer>

			{/* <Box height="100vh" marginLeft={smDown ? 0 : theme.spacing(28)}> */}
			<Box height="100vh" marginLeft={isDrawerOpen ? theme.spacing(28) : 0}>
				{children}
			</Box>
		</>
	);
};
